import { useEffect, useRef } from 'react';

/**
 * Hook for detecting clicks outside a specified element
 *
 * @param {import('react').RefObject} ref - Reference to the element that should NOT trigger the handler
 * @param {Function} handler - Function to call when click occurs outside the ref element
 * @param {boolean} enabled - Whether the click outside detection is enabled
 * @param {Array<import('react').RefObject>} excludeRefs - Array of additional refs to exclude from click-outside detection
 */
export function useClickOutside(ref, handler, enabled = true, excludeRefs = []) {
  const savedHandler = useRef(handler);

  // Update the handler ref whenever it changes
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleEvent = (event) => {
      // If ref is not set or the click is inside the element, do nothing
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      // Check if click is inside any of the excluded refs
      const isClickInExcludedRef = excludeRefs.some(
        (excludeRef) => excludeRef.current && excludeRef.current.contains(event.target)
      );

      if (isClickInExcludedRef) {
        return;
      }

      // Call the handler for clicks outside
      savedHandler.current(event);
    };

    // Use mousedown and touchstart for better UX
    // mousedown fires before click, preventing unwanted behavior
    document.addEventListener('mousedown', handleEvent);
    document.addEventListener('touchstart', handleEvent);

    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleEvent);
      document.removeEventListener('touchstart', handleEvent);
    };
  }, [ref, enabled, excludeRefs]);
}
